import React from "react";

import { IUser } from "./user.type";

export interface IModel {
  id: string;
  lastUpdatedOn: string;
  createdOn: string;
}

export enum UserRole {
  ADMIN = "ADMIN",
  ENDUSER = "ENDUSER",
  VERIFIER = "VERIFIER",
  FIRSTREVIEWER = "FIRSTREVIEWER",
  SECONDREVIEWER = "SECONDREVIEWER",
  APPROVER = "APPROVER",
  ADMINCOUNCIL = "ADMINCOUNCIL",
}

export enum Visibility {
  VISIBLE = "VISIBLE",
  HIDDEN = "HIDDEN",
}

export interface IStatistic {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: JSX.Element;
  /**vi
   * 6-digit HEX code of the color
   */
  color: string;
}

export interface SideBarRouteOption {
  path: string;
  icon: React.ComponentType<any>;
  exact?: boolean;
  routes?: SideBarRouteOption[];
  name: string;
  altPaths?: string[];
  iconSize?: number;
  roleAcessible?: string[];
  hasSubRoutes?: boolean;
}

export interface ProfileProps {
  profileUrl?: string;
  email: string;
  companyName: string;
}

export interface TabFiltersArgs {
  data: any[];
  sortedData: any[];
  setData?: React.Dispatch<React.SetStateAction<any[]>>;
  reset?: () => void;
}

export interface ISidebarProps {
  isActive: boolean;
}

export interface SideBarRoute {
  manage: SideBarRouteOption[];
  options: SideBarRouteOption[];
}

export interface ICertificateapplication {
  certificateAmount: number;
  depositDate: Date | null;
  admissionDate: Date | null;
  professionCategoryApplied: string;
  awardApplied: string;
  transactionNumber: string;
  bankId: string;
  alreadyRegistred: "Yes" | "No";
  nationalityCheck: "Yes" | "No" | "";
  remarks: string;
  previousCertificateDetails: string;
  previousCertificateFile: File | null;
  uploadCriminalRecordPath: File | null;
  uploadMedicalRecordPath: File | null;
  uploadReceipt: File | null;
  universityAdmissionLetter: File | null;
  uploadOtherDocumentPath: File | null;
  equivalence: File | null;
  passport: File | null;
  validVisa: File | null;
  residencePermit: File | null;
  transcript: File | null;
  cpdProof: File | null;
  proofOfRegistration: File | null;
  serviceId: string;
}

// For user registration fees (Array of certificates with a name and amount)
export interface RegistrationFee {
  name: string;
  amount: number;
}

// For user bank details
export interface Bank {
  id: string;
  name: string;
  accountNo: string;
  branchName: string;
}

// For profession category information
export interface ProfessionCategory {
  id: string;
  name: string;
  code: string;
  examRequired: boolean;
  isRequiredinLowLevel: boolean;
}

// For certificate application
export interface CertificateApplication {
  certificateAmount: number;
  depositDate: Date;
  transactionNumber: string;
  bankId: string;
  alreadyRegistred: string;
  remarks: string;
  previousCertificateDetails: string;
  previousCertificateFile: File | null;
  uploadReceipt: File | null;
  professionCategoryApplied: string;
  awardApplied: string;
  serviceId: string;
}

// For fetched application
export interface UserApplication {
  id: string;
  certificateAmount: number;
  transactionNumber: string;
  alreadyRegistered: string;
  status: string;
  remarks: string;
  professionCategoryApplied: string;
  awardApplied: string;
  serviceId: string;
  statusLevel: string;
  actionDoneBy: string;
  exitAward: string;
  // Add other properties as needed
}

export interface UserFines {
  id: string;
  applicationId: string;
  finesDate: Date;
  approvalDate: Date;
  userId: string;
  comment: string;
  decision: string;
  approvalLevel: number;
  userPosition: string;
  certificateFinesAmount: number;
  applicationName: string;
  applicantId: string;
  // Add other properties as needed
}

export interface OnholdApplications {
  applicationId: string;
  userId: string;
  comment: string;
  applicationName: string;
  applicantId: string;
  uploadFilePath: File | null;
  transDate: Date;
  // Add other properties as needed
}

export interface ApprovalOnholdApplication {
  id: string;
  applicationId: string;
  userId: string;
  comment: string;
  applicationName: string;
  applicantId: string;
  uploadReceiptFilePath: File | null;
  transactionDate: Date;
  userPosition: string;
  // Add other properties as needed
}

export interface UserFinesPayment {
  amountPayed: number;
  applicationId: string;
  comment: string;
  depositDate: Date | null;
  fineId: string;
  transactionDate: Date | null;
  uploadReceiptFilePath: File | null;
  userId: string;
  transactionNumber: string;
  bankId: string;
  applicationName: string;

  // Add other properties as needed
}

// For the user's approved certificates
export interface UserCertificateApproved {
  id: string;
  certificateName: string;
  status: string;
  applicationName: string;
  indexNumber: string;
  date: string; // Or Date if you are using Date objects
  expiredDate: string;
  userDetails: UserVerf;
  levelOfStudy: string;
  // Add other properties as needed
}

export interface Role {
  roleId: string;
  roleName: string;
}

export interface Encounter {
  id: string;
  createdOn: string;
  lastUpdatedOn: string;
  createdBy: IUser;
  lastUpdatedBy: IUser | null;
  visitDate: string;
}

export interface ApprovalRequest {
  createdOn: string;
  comment?: string;
  currentApprovalStatus: "PENDING" | "APPROVED" | "REJECTED";
  id: string;
  approvalDate?: string;
}

export interface UserVerf {
  firstName: string;
  lastName: string;
  nationalID: string;
}

export interface Verification {
  userPosition: string;
  user: UserVerf;
  approvalDate: string;
  decision: string;
  comment: string;
}

export enum EServiceStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}
export interface Service {
  id: string;
  name: string;
  council_id: string;
  registrationFee: number;
  code: string;
  approvalFinalLevel: number;
  dependentServices: Service[];
  status: EServiceStatus;
  tblcouncil?: {
    id: string;
    councilName: string;
    councilCode: string;
  };
}

export interface Council {
  id: string;
  councilName: string;
  councilCode: string;
  councilProfileDocumentPath: string;
  address: string;
  telephone: string;
  emails: string[];
  website: string;
}

export enum ECouncilName {
  NPC = "NPC",
  RMDC = "RMDC",
  NCNM = "NCNM",
  RAHPC = "RAHPC",
}


export enum EUserRoleName {
  ADMIN = "ADMIN",
  ADMINCOUNCIL = "ADMINCOUNCIL",
}

export interface StudyLevelConfiguration {
  id: string;
  studyLevelName: string;
  council: Council;
  service: Service;
}

export interface CreateStudyLevelConfigurationRequest {
  studyLevelName: string;
  councilId: string;
  serviceId: string;
}

export interface UpdateStudyLevelConfigurationRequest extends CreateStudyLevelConfigurationRequest {
  id: string;
}

export interface Fine {
  id: string;
  baseRatio: number;
  safePeriodDays: number;
  increaseUntilDay: number;
  council: Council;
  service: Service;
}

export interface CreateFineRequest {
  baseRatio: number;
  safePeriodDays: number;
  increaseUntilDay: number;
  councilId: string;
  serviceId: string;
}

export interface UpdateFineRequest extends CreateFineRequest {
  id: string;
}